import React from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Formik } from 'formik'
import { get } from 'lodash'

import Api from 'services/Api'
import BaseModal from 'components/Modals/BaseModal'
import { Button, DateTimePicker, Select, Textarea, Form } from 'components/Forms'
import { AbsenceBank } from 'components/Requests'
import { Alert } from 'ui'
import { Format, Parse } from 'services/Dates'
import { addComment, displayError, getAssignableReasons, requestTypeMap } from 'services/Requests'
import { closeModal } from 'store/actions/modals'
import { customSuccessNotification } from 'store/actions/notifications'
import { currentLang, t } from 'translations'

const ScheduleCreateAttendance = ({ shift }) => {
  const dispatch = useDispatch()
  const { attendanceReasonsById, attendanceTypes } = useSelector(state => state.resources)
  const { defaultLeaveStatus } = useSelector(state => state.settings)

  const { id: eventId, employeeId, end, start } = shift

  // Parse the attendance type to translate the name
  const parsedAttendanceTypes = attendanceTypes.map(at => ({ ...at, name: t(`manager.attendances.form.${at.name}`), _id: at.id }))

  const createAttendance = async (attendance, actions) => {
    try {
      // Is AM/PM format
      if (attendance.arrivalTime && attendance.arrivalTime.length > 5)
        attendance.arrivalTime = Parse(attendance.arrivalTime, { inFormat: 'time', forceStandardTime: true })
      if (attendance.departureTime && attendance.departureTime.length > 5)
        attendance.departureTime = Parse(attendance.departureTime, { inFormat: 'time', forceStandardTime: true })

      // if lateness or early are not default values format as HH:mm
      if (attendance.arrivalTime instanceof Date) attendance.arrivalTime = Format(attendance.arrivalTime, { outFormat: 'serverTime' })
      if (attendance.departureTime instanceof Date) attendance.departureTime = Format(attendance.departureTime, { outFormat: 'serverTime' })

      // Saving the new attendance

      const res = await Api.post(`/requests/leave`, attendance)
      if (res.success) {
        // Create the comment if there's one
        if (attendance.comment) await addComment('leave', res.leave._id, attendance.comment)

        // If the request update wasn't added successfully
      } else return displayError(actions, res.message)

      customSuccessNotification(dispatch, { resourceType: 'ATTENDANCE', isCreating: true })
      dispatch(closeModal())
    } catch (err) {
      displayError(actions, err.message)
    }
  }

  return (
    <BaseModal hideButtons title={`manager.schedule.CREATE_ATTENDANCE`}>
      <Formik
        enableReinitialize={true}
        initialValues={{
          eventId,
          userId: employeeId,
          end,
          start,
          requestTypeId: 1,
          reasonId: getAssignableReasons()[0]?._id,
          requestStatusId: defaultLeaveStatus || 'IN_PROGRESS',
          comment: ''
        }}
        onSubmit={createAttendance}
      >
        {actions => {
          // Reasons description
          const reasonDescription = get(attendanceReasonsById, `${actions.values.reasonId}.description${currentLang(true)}`, null)

          // Set the default value for arrivalTime / departureTime when they do not exist
          if (parseInt(actions.values.requestTypeId) === 2 && !actions.values.arrivalTime) {
            actions.setFieldValue('arrivalTime', Format(shift.start, { outFormat: 'time' }))
          }
          if (parseInt(actions.values.requestTypeId) === 3 && !actions.values.departureTime) {
            actions.setFieldValue('departureTime', Format(shift.end, { outFormat: 'time' }))
          }

          return (
            <Form className="mt-2">
              <Select
                module="manager.attendances.form"
                name="requestTypeId"
                options={parsedAttendanceTypes}
                required
                handleChange={e => {
                  // Since different reasons are tied to different requests types, it is
                  // necessary to set the value of the reason select element when request type changes
                  // to avoid bug of displaying wrong reason description
                  const changedRequestTypeId = parseInt(e.currentTarget.value)
                  const changedReasons = getAssignableReasons(requestTypeMap[changedRequestTypeId])

                  // If we switched to a request type that is saved on the attendance being edited, set the reason it is already set to
                  // Otherwise, use first reason in the reasons array
                  const reasonIdOfChangedRequestType = changedReasons[0]._id
                  actions.setFieldValue('reasonId', reasonIdOfChangedRequestType)
                  actions.setFieldValue('requestTypeId', parseInt(e.currentTarget.value))
                }}
              />

              <Select
                module="manager.attendances.form"
                name="reasonId"
                options={getAssignableReasons(requestTypeMap[actions.values.requestTypeId])}
                required
                disabled={!actions.values.requestTypeId}
              />
              {/* Reason description information */}
              <Alert variant="info" show={!!reasonDescription}>
                {reasonDescription}
              </Alert>

              {parseInt(actions.values.requestTypeId) === 2 ? (
                <DateTimePicker module="manager.attendances.form" type="time" name="arrivalTime" required />
              ) : null}
              {parseInt(actions.values.requestTypeId) === 3 ? (
                <DateTimePicker module="manager.attendances.form" type="time" name="departureTime" required />
              ) : null}

              <Textarea module="manager.attendances.form" name="comment" />

              {/* Absence threshold and bank */}
              <AbsenceBank currentUserId={employeeId} requestTypeId={actions.values.requestTypeId} />

              <div className="mt-2">
                <Button className="btn btn-primary mr-2" type="submit" isSubmitting={actions.isSubmitting} text="SUBMIT" />
                <Button text="actions.CLOSE" className="btn btn-outline-secondary" onClick={() => dispatch(closeModal())} />
              </div>
            </Form>
          )
        }}
      </Formik>
    </BaseModal>
  )
}

export default ScheduleCreateAttendance
